/** Connect events that can precede an unrequested Offline. */
export type ConnectSignal =
    | "authFail"
    | "authorizeRetriesExhausted"
    | "accessDenied"
    | "agentError"
    | "softphoneError"
    | "webSocketLost"
    | "failoverPending"
    | "failoverCompleted";

const RECENT_WINDOW_MS = 5 * 60_000;
const AUTH_FAILURE_SIGNALS: readonly ConnectSignal[] = ["authFail", "authorizeRetriesExhausted", "accessDenied"];

/** Recent Connect signals, plus ExWo's last request to tell an Offline set here from one set elsewhere. */
export class OfflineCauseTracker {
    #signalSeenAt = new Map<ConnectSignal, number>();
    #lastActivityRequest: { activity: string; at: number } | undefined;

    recordSignal(signal: ConnectSignal, now = Date.now()): void {
        this.#signalSeenAt.set(signal, now);
    }

    recordActivityRequest(activity: string, now = Date.now()): void {
        this.#lastActivityRequest = { activity, at: now };
    }

    /** Auth failure or access denied in the last five minutes, since the last successful authorize. */
    hasRecentAuthFailure(now = Date.now()): boolean {
        return AUTH_FAILURE_SIGNALS.some((signal) => {
            const seenAt = this.#signalSeenAt.get(signal);
            return seenAt !== undefined && now - seenAt <= RECENT_WINDOW_MS;
        });
    }

    /** Connect authorized again, so earlier auth failures no longer describe the session. */
    clearAuthFailures(): void {
        for (const signal of AUTH_FAILURE_SIGNALS) this.#signalSeenAt.delete(signal);
    }

    describe(now = Date.now()) {
        const recentConnectSignalsMsAgo: Partial<Record<ConnectSignal, number>> = {};
        for (const [signal, seenAt] of this.#signalSeenAt) {
            if (now - seenAt <= RECENT_WINDOW_MS) recentConnectSignalsMsAgo[signal] = now - seenAt;
        }

        const request = this.#lastActivityRequest;
        const lastExwoActivityRequest =
            request && now - request.at <= RECENT_WINDOW_MS
                ? { activity: request.activity, msAgo: now - request.at }
                : undefined;

        return {
            recentConnectSignalsMsAgo,
            lastExwoActivityRequest,
            documentVisibility: document.visibilityState,
            online: navigator.onLine,
        };
    }
}

export const offlineCauseTracker = new OfflineCauseTracker();
